import React, { useState, useEffect } from "react";
import { Box, Typography, Grid } from "@mui/material";
import { QuestionCard } from "../shared/QuestionCard";
import { StyledTextField } from "../form/StyledTextField";
import { QuizChoiceCard } from "../form/QuizChoiceCard";
import { QUIZ } from "@/theme/quizTokens";
import { quizAsset } from "../shared/quizAssets";

type Props = {
  dogName: string;
  years: number | "";
  months: number | "";
  onChange: (update: { years?: number; months?: number }) => void;
};

const AGE_PRESETS = [
  { key: "puppy", label: "Puppy", iconKey: "icon-age-puppy", y: 0, m: 6 },
  { key: "adult", label: "Adult", iconKey: "icon-age-adult", y: 3, m: 0 },
  { key: "senior", label: "Senior", iconKey: "icon-age-senior", y: 8, m: 0 },
];

export function AgeQuestion({ dogName, years, months, onChange }: Props) {
  const [localYears, setLocalYears] = useState<number | "">(years);
  const [localMonths, setLocalMonths] = useState<number | "">(months);

  useEffect(() => {
    setLocalYears(years === "" ? "" : Number(years));
  }, [years]);

  useEffect(() => {
    setLocalMonths(months === "" ? "" : Number(months));
  }, [months]);

  const y = localYears === "" ? 0 : Number(localYears);
  const m = localMonths === "" ? 0 : Number(localMonths);
  const totalMonths = y * 12 + m;

  const ageCategory =
    totalMonths === 0
      ? null
      : totalMonths < 12
      ? "puppy"
      : totalMonths < 96
      ? "adult"
      : "senior";

  const handlePreset = (presetY: number, presetM: number) => {
    setLocalYears(presetY);
    setLocalMonths(presetM);
    onChange({ years: presetY, months: presetM });
  };

  const handleYearsChange = (val: string) => {
    const num = val === "" ? "" : Math.max(0, parseInt(val) || 0);
    setLocalYears(num as number | "");
    if (num !== "") {
      onChange({ years: num as number });
    }
  };

  const handleMonthsChange = (val: string) => {
    const num = val === "" ? "" : Math.min(11, Math.max(0, parseInt(val) || 0));
    setLocalMonths(num as number | "");
    if (num !== "") {
      onChange({ months: num as number });
    }
  };

  return (
    <QuestionCard
      title={`How old is ${dogName || "your dog"}?`}
      field="age"
    >
      {/* Age presets — Figma icon tiles (Puppy / Adult / Senior) */}
      <Box sx={{ mb: { xs: "22px", md: "28px" }, width: "100%" }}>
        <QuizChoiceCard
          options={AGE_PRESETS.map((p) => ({
            value: p.key,
            label: p.label,
            icon: quizAsset(p.iconKey),
          }))}
          selected={ageCategory ?? ""}
          onSelect={(v) => {
            const preset = AGE_PRESETS.find((p) => p.key === v);
            if (preset) handlePreset(preset.y, preset.m);
          }}
        />
      </Box>

      {/* Manual input */}
      <Typography
        sx={{
          fontFamily: QUIZ.font.body,
          fontSize: { xs: 18, md: 20 },
          color: QUIZ.color.ink,
          mb: { xs: "16px", md: "20px" },
        }}
      >
        Tell us their exact age
      </Typography>

      <Grid container spacing={1.5} sx={{ width: "100%" }}>
        <Grid size={6}>
          <StyledTextField
            type="number"
            placeholder="Years"
            fullWidth
            value={localYears}
            onChange={(e) => handleYearsChange(e.target.value)}
            slotProps={{
              input: { inputProps: { min: 0, max: 25 } },
            }}
          />
        </Grid>
        <Grid size={6}>
          <StyledTextField
            type="number"
            placeholder="Months"
            fullWidth
            value={localMonths}
            onChange={(e) => handleMonthsChange(e.target.value)}
            slotProps={{
              input: { inputProps: { min: 0, max: 11 } },
            }}
          />
        </Grid>
      </Grid>
    </QuestionCard>
  );
}
